import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { bindPendingInvite, inviteTokenFromHash, resolveInviteToken, savePendingInvite } from './pendingInvite'

/**
 * The invite page's token: the one remembered as the pending invite (pendingInvite), or null. An invite link opened
 * from outside never reaches here with its fragment: takeInviteFragment, main.tsx's first step, kept the token and
 * took the fragment out before the router started. This hook covers what comes after: a new invite link pasted into a
 * tab already on the page takes over, and its fragment leaves the address bar and history through the router, so the
 * router's location loses it too (a passive effect, because the router listens to history from its own layout
 * effect, after its children's). Any other later fragment, such as the skip link's, stays; one present when the page
 * mounts (a test, or a router started some other way) goes. `userId`, the signed-in account once known, claims the token
 * (bindPendingInvite); a token another account claimed is dropped.
 */
export function useInviteToken(userId: string | null = null): string | null {
  const location = useLocation()
  const navigate = useNavigate()
  const [token, setToken] = useState(() => resolveInviteToken(location.hash))
  const mounted = useRef(false)

  useEffect(() => {
    const first = !mounted.current
    mounted.current = true
    if (!location.hash) return
    const fromLink = inviteTokenFromHash(location.hash)
    if (fromLink && !first) {
      savePendingInvite(fromLink)
      setToken(fromLink)
    }
    // On arrival any fragment goes, a cut-off token included; later only a token's.
    if (first || fromLink) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: location.state })
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    if (userId && token && !bindPendingInvite(userId)) setToken(null)
  }, [userId, token])

  return token
}
